'use client';

import { useIsWebview } from '@flipster/features/src/shared/webview';
import { useRouter } from '@flipster/i18n/routing';
import { IconButton } from '@flipster/react/design-system/icon-button';
import ChevronRightIcon from '@flipster/react/icon/generated/ChevronRightIcon';
import webviewHandler from '@flipster/utils/webview-handler';

interface Props {
  symbol: string;
}

// 기존 chevron과 시각적으로 동일하게 유지한다. IconButton size 변형은 고정값(medium 32/svg 20,
// large 48/svg 24)이라 브레이크포인트별 값과 여백 없는 박스를 css로 덮어쓴다.
// 이 override를 빼면 디자인 시스템 기본값인 32×32 탭 타깃이 되지만 행 너비가 8px 늘어난다.
const size = { base: 20, tablet: 24 };
const iconCss = {
  w: size,
  h: size,
  '& svg': { w: size, h: size },
} as const;

/** 웹: 평범한 앵커. 크롤러와 하이드레이션 이전 탭이 이 경로를 탄다. */
const TradeAnchor = ({ symbol }: Props) => (
  <IconButton
    as='a'
    href={`/trade/perpetual/${symbol}`}
    prefetch={false}
    hierarchy='quaternary'
    color='content.default.level-5'
    icon={<ChevronRightIcon />}
    css={iconCss}
  />
);

/**
 * 앱 웹뷰: <a>가 아닌 <button>으로 렌더하고 네이티브 오더폼에 브릿지로 위임한다.
 *
 * 웹 내비게이션으로 두면 Next 라우터가 RSC fetch를 보내는데, 그 요청이 Vercel 방화벽
 * 챌린지(429 + text/html)에 걸리면 fetch는 챌린지 JS를 실행할 수 없어 `_vcrcs` 쿠키를
 * 얻지 못하고 복구가 불가능하다(QA-21298). href가 없으므로 롱프레스 컨텍스트 메뉴의
 * "새 창에서 열기"로 웹 오더폼이 열리는 경로도 함께 막힌다.
 *
 * 브릿지가 없으면 postMessage가 failHandler를 호출하므로 router로 폴백된다.
 *
 * 서버 렌더와 하이드레이션 직후에는 `useIsWebview()`가 false다. `webviewInfoBunja`의
 * `webviewAtom` 초기값이 `IsWebviewScope` 기본값(false)이고, `bunja.effect`가 search
 * params/sessionStorage/브릿지를 읽은 뒤에야 true로 바뀐다. 그래서 SSR 출력과 최초 렌더는
 * 항상 앵커이고(크롤러가 링크를 본다), 웹뷰에서는 그 직후 버튼으로 교체된다.
 */
export const TradeLink = ({ symbol }: Props) => {
  const router = useRouter();
  const isWebview = useIsWebview();

  if (!isWebview) return <TradeAnchor symbol={symbol} />;

  return (
    <IconButton
      hierarchy='quaternary'
      color='content.default.level-5'
      icon={<ChevronRightIcon />}
      css={iconCss}
      onClick={() =>
        webviewHandler.postMessage({ action: 'link', params: { page: `trade/perpetual/${symbol}` } }, () =>
          router.push(`/trade/perpetual/${symbol}`),
        )
      }
    />
  );
};
